import { Injectable } from '@angular/core';
import { Matter } from '@models/matter';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import * as actions from './matter-checklist.actions';
import { ChecklistItem } from '@models/checklist';
import { getCongratsMessage } from '@core/util/string';
import { HttpHandlerService } from '@services/httpHandler/httpHandler.service';
import {
	FirestoreStateBaseForMatterWithSubscription,
	MatterWithSubscriptionStateModel,
} from '@state/FirestoreStateBaseForMatter';
import { CollectionPaths } from '@models/collections';
import { Logger } from '@core/logger';
import { SelectedMatter } from '../matter.selectors';
import { DEFAULT_STATE_BASE } from '@state/FirestoreStateBase';

export interface ChecklistModel extends MatterWithSubscriptionStateModel {
	items: ChecklistItem[];
}

type Ctx = StateContext<ChecklistModel>;

const DEFAULT_STATE: ChecklistModel = {
	...DEFAULT_STATE_BASE,
	items: [],
};

@State<ChecklistModel>({
	name: 'matterChecklists',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class MatterChecklistsState extends FirestoreStateBaseForMatterWithSubscription<
	ChecklistItem,
	ChecklistModel
> {
	constructor(private http: HttpHandlerService) {
		super(
			(pathParams) =>
				CollectionPaths.business_matter_checklists(
					pathParams.businessId,
					pathParams.matterId,
				),
			(payload) => ({ items: payload as ChecklistItem[] }),
			() => DEFAULT_STATE,
		);
	}

	@Selector([MatterChecklistsState])
	static getAll(state: ChecklistModel): ChecklistItem[] {
		return state?.items ?? [];
	}

	@Selector([MatterChecklistsState.getAll])
	static getChecklistTemplateItem(checklists: ChecklistItem[]): ChecklistItem {
		return checklists.filter((checklist) => checklist.type === 'template')[0]; //there should only be one right now
	}

	@Selector([MatterChecklistsState.getAll])
	static getStages(checklists: ChecklistItem[]): ChecklistItem[] {
		return checklists.filter((checklist) => checklist.type === 'stage');
	}

	@Selector([MatterChecklistsState.getAll])
	static getTasksByStageId(checklists: ChecklistItem[]) {
		return (id: string) =>
			checklists.filter(
				(checklist) => checklist.type === 'task' && checklist.parentId === id,
			);
	}

	@Selector([MatterChecklistsState.getAll])
	static getStageCompletePercentage(checklists: ChecklistItem[]) {
		return (id: string) => {
			const tasks = checklists.filter(
				(checklist) => checklist.type === 'task' && checklist.parentId === id,
			);
			const completedTasks = tasks.filter((task) => task.complete).length;
			return ((completedTasks / tasks.length) * 100).toFixed(0);
		};
	}

	@Selector([MatterChecklistsState.getAll, SelectedMatter.getSelected])
	static getChecklistCompletePercentage(
		checklists: ChecklistItem[],
		matter: Matter,
	): string {
		if (!checklists || !matter?.checklistProgress || checklists.length === 0)
			return '0';

		const { completedCount, totalCount } = matter.checklistProgress;
		const rawPercentage = (completedCount / totalCount) * 100;

		if (isNaN(rawPercentage)) {
			return '0';
		}

		return Math.round(rawPercentage).toString();
	}

	@Action(actions.RemoveAll)
	async removeAll(ctx: Ctx): Promise<void> {
		const state = ctx.getState();
		const items = state.items;

		const body = {
			checklistItemsIds: items.map((item) => item.id),
		};

		try {
			await this.http
				.post(
					`checklist/matters/${this.matterId}/matterChecklistItems/delete`,
					body,
				)
				.toPromise();
		} catch (err) {
			Logger.exception(err);
		}
	}

	@Action(actions.Remove)
	async remove(ctx: Ctx, { payload }: actions.Remove): Promise<void> {
		const body = {
			checklistItemsIds: [payload],
		};
		try {
			await this.http
				.post(
					`checklist/matters/${this.matterId}/matterChecklistItems/delete`,
					body,
				)
				.toPromise();
		} catch (err) {
			Logger.exception(err);
		}
	}

	@Action(actions.CreateItem)
	async createItem(ctx: Ctx, { payload }: actions.CreateItem) {
		await this.repo.CreateOrMerge(payload.id, payload).then(() => {
			this.updateProgress();
		});
	}

	@Action(actions.CreateAll)
	create(ctx: Ctx, { payload }: actions.CreateAll): void {
		const template: ChecklistItem = {
			businessId: payload.id,
			id: payload.id,
			title: payload.title,
			type: 'template',
			congratsMessage: getCongratsMessage(),
			nextNodeId: '',
			headNode: true,
		};

		const stagesAndTasks: ChecklistItem[] = [];
		payload.stages.forEach((s, stageIndex, stages) => {
			stagesAndTasks.push({
				id: `${payload.id}-${stageIndex}`,
				businessId: payload.id,
				parentId: template.id,
				title: s.title,
				type: 'stage',
				headNode: stageIndex === 0,
				nextNodeId:
					stageIndex === stages.length - 1 ? '' : stages[stageIndex + 1].id,
			});

			s.tasks.forEach((t, taskIndex, tasks) => {
				stagesAndTasks.push({
					id: `${payload.id}-${stageIndex}-${taskIndex}`,
					businessId: payload.id,
					parentId: s.id,
					title: t.title,
					type: 'task',
					headNode: taskIndex === 0,
					nextNodeId:
						taskIndex === tasks.length - 1 ? '' : tasks[taskIndex + 1].id,
				});
			});
		});

		const items = [template, ...stagesAndTasks];

		items.forEach((item) => {
			ctx.dispatch(new actions.CreateItem(item));
		});
	}

	@Action(actions.Update)
	async update(ctx: Ctx, { payload }: actions.Update): Promise<void> {
		await this.repo.CreateOrMerge(payload.id, payload);
		this.updateProgress();
	}

	@Action(actions.ToggleTask)
	async toggleTask(ctx: Ctx, { payload }: actions.ToggleTask): Promise<void> {
		await this.repo.OverwriteFields(payload.id, 'complete', payload.complete);
	}

	@Action(actions.ToggleAllTasks)
	async toggleAllTasks(
		ctx: Ctx,
		{ payload }: actions.ToggleAllTasks,
	): Promise<void> {
		const checklistItems: ChecklistItem[] = ctx
			.getState()
			.items.filter((item) => item.type === 'task')
			.map((task) => ({
				...task,
				complete: payload,
			}));

		const addChecklistPath = `checklist/matters/${this.matterId}/matterChecklistItems/add`;
		const body: { checklistItems: ChecklistItem[] } = {
			checklistItems,
		};

		await this.http.post(addChecklistPath, body).toPromise();
	}

	@Action(actions.UpdateProgress)
	async updateProgress(): Promise<void> {
		await this.http
			.patch(`checklist/matters/${this.matterId}/update-checklist-progress`)
			.toPromise();
	}

	@Selector([MatterChecklistsState.getStages])
	static getSortedStages(unsortedStages: ChecklistItem[]) {
		if (unsortedStages.length === 0) return unsortedStages;

		const stages = structuredClone(unsortedStages);
		const firstStageIndex = stages.findIndex((s) => s.headNode);

		if (firstStageIndex === -1) return stages;

		const sortedStages = stages.splice(firstStageIndex, 1);

		while (stages.length > 0) {
			const nextStageId = sortedStages.at(-1).nextNodeId;

			if (!nextStageId) return sortedStages;

			const nextStageIndex = stages.findIndex((s) => s.id === nextStageId);
			const nextStage = stages.splice(nextStageIndex, 1).at(0);

			sortedStages.push(nextStage);
		}

		return sortedStages;
	}

	@Action(actions.UpdateStageOpenedState)
	async updateStageOpenedState(
		ctx: Ctx,
		{ payload }: actions.UpdateStageOpenedState,
	): Promise<void> {
		await this.repo.OverwriteFields(payload.id, 'opened', payload.opened);
	}
}
